import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

// How far past a section's top the container must scroll before that section
// counts as the active one
const ACTIVATION_OFFSET_PX = 80;
// Fallback in case neither arrival nor `scrollend` is detected; long jumps
// animate for well over a second, so keep this generous
const SMOOTH_SCROLL_TIMEOUT_MS = 3000;
// Lands the section header below the container's top edge fade instead of
// flush against it, where the fade would wash the header out
const SCROLL_MARGIN_PX = 40;

/** Read with `useActiveSectionSlug` from the component that highlights the
 * active section, so scroll updates re-render only that component. */
export interface ActiveSlugStore {
  subscribe: (listener: () => void) => () => void;
  get: () => string | null;
}

export const useActiveSectionSlug = (store: ActiveSlugStore) =>
  useSyncExternalStore(store.subscribe, store.get);

/**
 * Tracks which of a set of stacked sections is currently scrolled into view,
 * and scrolls to a section on demand. Sections register themselves via
 * `registerSection`; the scroll container via `setContainer`.
 *
 * The active slug lives outside React state on purpose: a scroll-position
 * change re-renders only subscribers of `activeSlugStore`, not the component
 * that owns the (potentially large) scrolled content.
 */
export const useSectionScrollSpy = (sectionSlugs: string[]) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sectionElsRef = useRef(new Map<string, HTMLElement>());
  // Section offsets are stable between content/size changes, so they're read
  // once per invalidation rather than on every scroll event (layout reads
  // during scroll force synchronous reflows)
  const sectionTopsRef = useRef<{ slug: string; top: number }[] | null>(null);
  // Suppresses spy updates while a click-triggered smooth scroll is in flight,
  // so intermediate sections don't flash as active
  const suppressedRef = useRef(false);
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  const activeSlugRef = useRef<string | null>(null);
  const listenersRef = useRef(new Set<() => void>());
  const activeSlugStore = useMemo<ActiveSlugStore>(
    () => ({
      subscribe: (listener) => {
        listenersRef.current.add(listener);
        return () => listenersRef.current.delete(listener);
      },
      get: () => activeSlugRef.current,
    }),
    []
  );
  const setActiveSlug = (slug: string | null) => {
    if (activeSlugRef.current === slug) return;
    activeSlugRef.current = slug;
    for (const listener of listenersRef.current) listener();
  };

  const setContainer = useCallback((el: HTMLDivElement | null) => {
    containerRef.current = el;
    setContainerEl(el);
  }, []);

  const registerSection = useCallback(
    (slug: string) => (el: HTMLElement | null) => {
      if (el) sectionElsRef.current.set(slug, el);
      else sectionElsRef.current.delete(slug);
      sectionTopsRef.current = null;
    },
    []
  );

  const computeActiveSlug = useCallback(() => {
    const container = containerRef.current;
    if (!container || sectionSlugs.length === 0) return null;
    if (!sectionTopsRef.current) {
      sectionTopsRef.current = sectionSlugs.flatMap((slug) => {
        const sectionEl = sectionElsRef.current.get(slug);
        return sectionEl ? [{ slug, top: sectionEl.offsetTop }] : [];
      });
    }
    // The last section may be too short to ever reach the activation line, so
    // hitting the bottom always activates it — but only when the container
    // actually overflows, else non-scrollable content is "at bottom" at rest
    const scrollable = container.scrollHeight > container.clientHeight + 1;
    const atBottom =
      scrollable &&
      container.scrollTop + container.clientHeight >=
        container.scrollHeight - 1;
    if (atBottom) return sectionSlugs[sectionSlugs.length - 1];
    let active = sectionSlugs[0];
    for (const { slug, top } of sectionTopsRef.current) {
      if (top <= container.scrollTop + ACTIVATION_OFFSET_PX) active = slug;
    }
    return active;
  }, [sectionSlugs]);

  useEffect(() => {
    if (!containerEl) return;
    sectionTopsRef.current = null;
    let frame: number | null = null;
    const update = () => {
      frame = null;
      if (!suppressedRef.current) setActiveSlug(computeActiveSlug());
    };
    const handleScroll = () => {
      frame ??= requestAnimationFrame(update);
    };
    update();
    containerEl.addEventListener('scroll', handleScroll, { passive: true });
    // Card heights shift with container width, moving the section offsets
    const resizeObserver = new ResizeObserver(() => {
      sectionTopsRef.current = null;
      handleScroll();
    });
    resizeObserver.observe(containerEl);
    return () => {
      containerEl.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [containerEl, computeActiveSlug]);

  // Removes the in-flight scroll's listeners without lifting suppression, so
  // a second click can take over the flight cleanly
  const cancelFlightRef = useRef<(() => void) | null>(null);

  const scrollToSection = useCallback(
    (slug: string, behavior: ScrollBehavior = 'smooth') => {
      const container = containerRef.current;
      const sectionEl = sectionElsRef.current.get(slug);
      if (!container || !sectionEl) return;
      setActiveSlug(slug);
      const target = Math.max(0, sectionEl.offsetTop - SCROLL_MARGIN_PX);
      if (behavior === 'smooth') {
        cancelFlightRef.current?.();
        suppressedRef.current = true;
        const cancelFlight = () => {
          container.removeEventListener('scroll', handleArrival);
          container.removeEventListener('scrollend', settle);
          clearTimeout(timer);
          cancelFlightRef.current = null;
        };
        const settle = () => {
          cancelFlight();
          suppressedRef.current = false;
          // The flight may have been interrupted (user wheel, layout change),
          // so re-derive the highlight from wherever the scroll ended up
          setActiveSlug(computeActiveSlug());
        };
        // The animation can outlast any fixed timeout on long jumps, and a
        // clamped target is never reached exactly — settle on whichever of
        // arrival / `scrollend` / timeout happens first
        const handleArrival = () => {
          if (Math.abs(container.scrollTop - target) <= 1) settle();
        };
        const timer = setTimeout(settle, SMOOTH_SCROLL_TIMEOUT_MS);
        container.addEventListener('scroll', handleArrival, { passive: true });
        container.addEventListener('scrollend', settle);
        cancelFlightRef.current = cancelFlight;
      }
      container.scrollTo({ top: target, behavior });
    },
    [computeActiveSlug]
  );

  return { setContainer, registerSection, activeSlugStore, scrollToSection };
};
